import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  plan: 'free' | 'pro' | 'studio' | 'creator' | 'business'
  createdAt: string
}

interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>
  signup: (name: string, email: string, password: string) => Promise<boolean>
  loginWithGoogle: () => Promise<void>
  loginWithGithub: () => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
  setLoading: (loading: boolean) => void
  initialize: () => Promise<void>
  handleAuthChange: (session: Session | null) => void
}

const mapSupabaseUser = (supabaseUser: SupabaseUser): User => ({
  id: supabaseUser.id,
  email: supabaseUser.email || '',
  name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
  avatar: supabaseUser.user_metadata?.avatar_url,
  plan: 'free',
  createdAt: supabaseUser.created_at
})

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        set({
          user: mapSupabaseUser(session.user),
          session,
          isAuthenticated: true,
          isLoading: false
        })
      } else {
        set({ isLoading: false })
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
      set({ isLoading: false })
    }
  },

  handleAuthChange: (session: Session | null) => {
    if (session?.user) {
      set({
        user: mapSupabaseUser(session.user),
        session,
        isAuthenticated: true,
        isLoading: false
      })
    } else {
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false
      })
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    
    if (error) {
      set({ isLoading: false, error: error.message })
      return false
    }
    
    if (data.user) {
      set({
        user: mapSupabaseUser(data.user),
        session: data.session,
        isAuthenticated: true,
        isLoading: false,
        error: null
      })
      return true
    }
    
    set({ isLoading: false })
    return false
  },

  signup: async (name: string, email: string, password: string) => {
    set({ isLoading: true, error: null })
    
    if (!name || name.length < 2) {
      set({ isLoading: false, error: 'Name must be at least 2 characters.' })
      return false
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name
        }
      }
    })
    
    if (error) {
      set({ isLoading: false, error: error.message })
      return false
    }
    
    if (data.user) {
      if (data.user.identities?.length === 0) {
        set({ isLoading: false, error: 'An account with this email already exists.' })
        return false
      }
      
      // If session exists, user is logged in immediately (email confirmation disabled)
      // If no session, email confirmation is required
      if (data.session) {
        set({
          user: mapSupabaseUser(data.user),
          session: data.session,
          isAuthenticated: true,
          isLoading: false,
          error: null
        })
      } else {
        // Email confirmation required - show success message
        set({
          isLoading: false,
          error: 'Please check your email to confirm your account.'
        })
      }
      return !!data.session
    }
    
    set({ isLoading: false })
    return false
  },

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null })
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    
    if (error) {
      set({ isLoading: false, error: error.message })
    }
  },

  loginWithGithub: async () => {
    set({ isLoading: true, error: null })
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    
    if (error) {
      set({ isLoading: false, error: error.message })
    }
  },

  logout: async () => {
    await supabase.auth.signOut()
    set({
      user: null,
      session: null,
      isAuthenticated: false,
      error: null
    })
  },

  clearError: () => {
    set({ error: null })
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  }
}))
